import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import * as DActions from 'ApplicationEntry/ducks/ProductsRiders/disability';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {matchDisabilityByType} from 'ApplicationEntry/models/ProductsRiders/Disability/Disability';
import {
  InvalidOrdDisability,
  ValidOrdDisability,
} from 'ApplicationEntry/models/ProductsRiders/Disability/OrdinaryDisability';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import {
  isLoaded as isLoadedInt,
  isLoading as isloadingInt,
} from 'ApplicationEntry/models/ProductsRiders/ProductRiderIntModel';
import {isLoaded, isLoading} from 'ApplicationEntry/models/ProductsRiders/ProductRiderStringModel';
import {isNonEmptyString} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import './Disability.sass';

type DisabilityProps = {
  disability: ValidOrdDisability | InvalidOrdDisability;
};

const getApplicationType = matchDisabilityByType({
  Group: () => ApplicationType.Group,
  Ordinary: () => ApplicationType.Ordinary,
  NewYork: () => ApplicationType.NewYork,
});

const Disability = (props: DisabilityProps) => {
  const dispatch = useDispatch();
  const {
    plan,
    type,
    accident,
    sickness,
    benefitPeriod,
    accidentRiderUnit,
    onJobRider,
    onJobRiderMonthly,
    mentalNervousRider,
    occClass,
    monthlySalary,
    section125,
    calculatedPremium,
    cwa,
    monthlyBenefit,
    riderType,
    addRider,
  } = props.disability;
  const {
    planList,
    typeList,
    accidentList,
    sicknessList,
    benefitPeriodList,
    riderUnitList,
    riderTypeList,
    occClassList,
  } = props.disability;
  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [accidentLabel, setAccidentLabel] = useState<HTMLLabelElement | null>();
  const [sicknessLabel, setSicknessLabel] = useState<HTMLLabelElement | null>();
  const [sicknessDays, setSicknessDays] = useState<string[]>([]);
  const showPlanTypeError = planTypeBlurred && plan.key === 'Empty';
  const applicationType = getApplicationType(props.disability);
  useEffect(() => {
    dispatch(DActions.getPlanType(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
    dispatch(DActions.getAccident(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
    dispatch(DActions.getSickness(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
    dispatch(DActions.getAccidentRiderUnits(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
    dispatch(DActions.getRiderType(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
    dispatch(DActions.getOccClass(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability]));
  }, [applicationType, dispatch]);

  const changeAccidentRiderUnit = (value: number) => {
    dispatch(DActions.changeAccidentRiderUnit(value));
  };

  useEffect(() => {
    if (riderType.value === '' && isLoaded(riderTypeList)) {
      dispatch(DActions.changeRiderType(riderTypeList.Data[0]));
    }
  }, [applicationType, dispatch, riderType, riderTypeList]);

  useEffect(() => {
    if (isNonEmptyString(plan)) {
      dispatch(
        DActions.getType(applicationType, ProductRiderGroups[ProductsRidersPolicyType.Disability], plan.value)
      );
    }
  }, [applicationType, dispatch, plan]);

  const handleAccidentChange = (accidentValue: string) => {
    dispatch(DActions.changeAccident(accidentValue));
    dispatch(DActions.changeSickness(''));

    switch (accidentValue) {
      case '000':
        setSicknessDays(['007', '014']);
        break;
      case '014':
        setSicknessDays(['014']);
        break;
      case '180':
        setSicknessDays(['180']);
        break;
      case '030':
        setSicknessDays(['030']);
        break;
      case '007':
        setSicknessDays(['007']);
        break;
      case '090':
        setSicknessDays(['090']);
        break;
    }
  };

  useEffect(() => {
    if (isNonEmptyString(plan) && isNonEmptyString(type)) {
      dispatch(
        DActions.getBenefitPeriod(
          applicationType,
          ProductRiderGroups[ProductsRidersPolicyType.Disability],
          type.value,
          plan.value
        )
      );
    }
  }, [applicationType, dispatch, plan, type]);

  const getHeader = (header1: string, header2: string) => {
    return (
      <>
        <div className="disability-section__sub-title-container">
          <Typography variant="subtitle1" className="disability-section__sub-title-container-title">
            {header1}
          </Typography>
        </div>
        {header2 !== '' ? (
          <div className="disability-section__sub-title-container">
            <Typography variant="subtitle1" className="disability-section__sub-title-container-title">
              {header2}
            </Typography>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="disability-section" id="disability-section">
      <form noValidate autoComplete="off" className="disability-section__form">
        <div className="disability-section__form-row">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FormControl error={showPlanTypeError} variant="outlined" className="disability-section__form-text-field">
                <InputLabel ref={setPlanTypeLabel} id="disability-plantype-label">
                  Plan
                </InputLabel>
                <Select
                  labelId="disability-label-plantype"
                  labelWidth={planTypeLabel?.clientWidth}
                  id="disability-plantype"
                  value={plan.key === 'Empty' ? '' : plan.value}
                  onBlur={() => setBlurPlanType(true)}
                  onChange={e => dispatch(DActions.changePlan(e.target.value as string))}
                  displayEmpty
                >
                  {isLoaded(planList) ? (
                    planList.Data.map((product, index) => (
                      <MenuItem value={product} key={index}>
                        {product}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                  )}
                </Select>
                {showPlanTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                {isLoading(planList) ? (
                  <CircularProgress className="critical-illness-section__loading-icon" />
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}></Grid>
          </Grid>

          {plan.key === 'NonEmpty' ? (
            <>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="disability-section__form-text-field">
                    <InputLabel id="disability-type-label">Type</InputLabel>
                    <Select
                      labelId="disability-label-type"
                      labelWidth={planTypeLabel?.clientWidth}
                      id="disability-type"
                      value={type.key === 'Empty' ? '' : type.value}
                      onChange={e => dispatch(DActions.changeType(e.target.value as string))}
                      displayEmpty
                    >
                      {isLoaded(typeList) ? (
                        typeList.Data.map((product, index) => (
                          <MenuItem value={product} key={index}>
                            {product}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                      )}
                    </Select>
                    {isLoading(typeList) ? (
                      <CircularProgress className="critical-illness-section__loading-icon" />
                    ) : null}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Grid item xs={12}>
                    {getHeader('Elimination Period', '')}
                  </Grid>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className="disability-section__form-text-field">
                        <InputLabel ref={setAccidentLabel} id="disability-accident-label">Accident</InputLabel>
                        <Select
                          labelId="disability-label-accident"
                          labelWidth={accidentLabel?.clientWidth}
                          id="disability-accident"
                          value={accident.key === 'Empty' ? '' : accident.value}
                          onChange={e => handleAccidentChange(e.target.value as string)}
                          displayEmpty
                        >
                          {isLoaded(accidentList) ? (
                            accidentList.Data.map((product, index) => (
                              <MenuItem value={product} key={index}>
                                {product}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                          )}
                        </Select>
                        {isLoading(accidentList) ? (
                          <CircularProgress className="critical-illness-section__loading-icon" />
                        ) : null}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className="disability-section__form-text-field">
                        <InputLabel ref={setSicknessLabel} id="disability-sickness-label">Sickness</InputLabel>
                        <Select
                          labelId="disability-label-sickness"
                          labelWidth={sicknessLabel?.clientWidth}
                          id="disability-sickness"
                          value={sickness.key === 'Empty' ? '' : sickness.value}
                          onChange={e => dispatch(DActions.changeSickness(e.target.value as string))}
                          displayEmpty
                        >
                          {sicknessDays.map(days => (
                            <MenuItem value={days}>{days}</MenuItem>
                          ))}
                        </Select>
                        {isLoading(sicknessList) ? (
                          <CircularProgress className="critical-illness-section__loading-icon" />
                        ) : null}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Grid item xs={12}>
                    {getHeader('Benefit', '')}
                  </Grid>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" className="disability-section__form-text-field">
                        <InputLabel id="disability-period-label">Period</InputLabel>
                        <Select
                          labelId="disability-label-period"
                          labelWidth={planTypeLabel?.clientWidth}
                          id="disability-period"
                          value={benefitPeriod.key === 'Empty' ? '' : benefitPeriod.value}
                          onChange={e => dispatch(DActions.changeBenefitPeriod(e.target.value as string))}
                          displayEmpty
                        >
                          {isLoadedInt(benefitPeriodList) ? (
                            benefitPeriodList.Data.map((product, index) => (
                              <MenuItem value={product} key={index}>
                                {product}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                          )}
                        </Select>
                        {isloadingInt(benefitPeriodList) ? (
                          <CircularProgress className="critical-illness-section__loading-icon" />
                        ) : null}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        id="disability-monthly-benefit"
                        label="Monthly:$"
                        variant="outlined"
                        placeholder="Monthly:$"
                        margin="normal"
                        fullWidth
                        className="disability-section__form-text-field"
                        value={monthlyBenefit.value}
                        onChange={e => dispatch(DActions.changeBenefitMonthly(e.target.value as string))}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={4}>
                <Grid item xs={12} className="padding-bottom-none">
                  {getHeader('Accident Rider', '')}
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="disability-section__form-text-field">
                    <InputLabel id="disability-units-label">Units</InputLabel>
                    <Select
                      labelId="disability-label-units"
                      labelWidth={planTypeLabel?.clientWidth}
                      id="disability-units"
                      value={accidentRiderUnit !== -1 ? accidentRiderUnit : ''}
                      onChange={e => changeAccidentRiderUnit(e.target.value as number)}
                      displayEmpty
                    >
                      {isLoadedInt(riderUnitList) ? (
                        riderUnitList.Data.map((product, index) => (
                          <MenuItem value={product} key={index}>
                            {product}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                      )}
                    </Select>
                    {isloadingInt(riderUnitList) ? (
                      <CircularProgress className="critical-illness-section__loading-icon" />
                    ) : null}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl
                    component="fieldset"
                    disabled={accidentRiderUnit === -1 ? true : false}
                    className="disability-section__form-radio-group"
                  >
                    <RadioGroup
                      aria-label="familyType"
                      name="familyType"
                      value={riderType.value}
                      onChange={e => dispatch(DActions.changeRiderType(e.target.value as string))}
                    >
                      {isLoaded(riderTypeList)
                        ? riderTypeList.Data.map(product => (
                            <FormControlLabel
                              key={product}
                              value={product}
                              control={<Radio />}
                              label={product}
                              className="disability-section__form-radio-button"
                            />
                          ))
                        : null}
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={mentalNervousRider}
                        onChange={e => dispatch(DActions.changeMentalNervousRider(!mentalNervousRider))}
                        name="checkedB"
                      />
                    }
                    label={<Typography>Mental/Nervous Rider</Typography>}
                    className="disability-section__form-text-field"
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={onJobRider}
                        onChange={e => dispatch(DActions.changeOnJobRider(!onJobRider))}
                        name="checkedB"
                        id="disability-onjob"
                      />
                    }
                    label={<Typography>On-The-Job Rider</Typography>}
                    className="disability-section__form-text-field"
                    disabled={addRider}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="disability-monthly-benefit"
                    label="Monthly:$"
                    variant="outlined"
                    placeholder="Monthly:$"
                    margin="normal"
                    fullWidth
                    className="disability-section__form-text-field"
                    disabled={!onJobRider}
                    value={onJobRiderMonthly.key === 'Empty' ? '' : onJobRiderMonthly.value}
                    onChange={e => dispatch(DActions.changeOnJobMonthlyRider(e.target.value as string))}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={4}>
                <Grid item xs={12} className="padding-bottom-none">
                  {getHeader('Additional Information', '')}
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="disability-section__form-text-field">
                    <InputLabel ref={setPlanTypeLabel} id="disability-occClass-label">Occ. Class</InputLabel>
                    <Select
                      labelId="disability-label-occClass"
                      labelWidth={planTypeLabel?.clientWidth}
                      id="disability-occClass"
                      value={occClass.value}
                      onChange={e => dispatch(DActions.changeOccClass(e.target.value as string))}
                      displayEmpty
                    >
                      {isLoaded(occClassList) ? (
                        occClassList.Data.map((product, index) => (
                          <MenuItem value={product} key={index}>
                            {product}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                      )}
                    </Select>
                    {isLoading(occClassList) ? (
                      <CircularProgress className="critical-illness-section__loading-icon" />
                    ) : null}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="disability-monthly-salary"
                    label="Monthly Salary"
                    variant="outlined"
                    placeholder="Monthly Salary"
                    margin="normal"
                    fullWidth
                    className="disability-section__form-text-field"
                    value={monthlySalary.key === 'Empty' ? '' : monthlySalary.value}
                    onChange={e => dispatch(DActions.changeMonthlySalary(e.target.value as string))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="secondary"
                        id="disability-section125"
                        checked={section125}
                        onChange={e => dispatch(DActions.changeSection125(!section125))}
                      />
                    }
                    label={<Typography>Section 125</Typography>}
                    className="disability-section__form-text-field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="disability-premium"
                    label="Premium"
                    variant="outlined"
                    placeholder="Premium"
                    margin="normal"
                    fullWidth
                    className="disability-section__form-text-field"
                    value={calculatedPremium.key === 'Empty' ? '' : calculatedPremium.value}
                    onChange={event => {
                      dispatch(DActions.changePremium(event.target.value as string));
                      dispatch(DActions.changeCalculatedPremium(event.target.value as string));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="disability-cwa"
                    label="CWA"
                    variant="outlined"
                    placeholder="CWA"
                    margin="normal"
                    fullWidth
                    className="disability-section__form-text-field"
                    value={cwa.key === 'Empty' ? '' : cwa.value}
                    onChange={e => dispatch(DActions.changeCWA(e.target.value as string))}
                  />
                </Grid>
              </Grid>
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default Disability;
